import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import ApplicantShell from "../components/ApplicantShell";
import { COUNTRIES } from "../data/countriesList";
import { isApiConfigured, listApplicantSavedSearches } from "../apiClient";

function formatSavedSearchCriteria(criteria) {
  if (!criteria || typeof criteria !== "object") return null;
  const ISO_LABEL = new Map(COUNTRIES.map((c) => [c.value, c.label]));
  const countries = Array.isArray(criteria.criteriaCountryCodes)
    ? criteria.criteriaCountryCodes.filter(Boolean)
    : [];
  const keywords = String(criteria.criteriaTitles || "").trim();
  const workMode = String(criteria.criteriaWorkMode || "").trim();
  const currency = String(criteria.criteriaSalaryCurrency || "").trim();
  const min = String(criteria.salaryMin || "").trim();
  const max = String(criteria.salaryMax || "").trim();

  const parts = [];
  if (countries.length) {
    const longNames = countries.map((iso) => ISO_LABEL.get(iso) || iso).join(", ");
    parts.push(
      <span key="countries">
        <strong>Countries:</strong> {longNames}
      </span>
    );
  }
  if (keywords) {
    parts.push(
      <span key="keywords">
        <strong>Keywords:</strong> {keywords}
      </span>
    );
  }
  if (workMode) {
    parts.push(
      <span key="workMode">
        <strong>Work mode:</strong> {workMode}
      </span>
    );
  }
  if (currency || min || max) {
    const band = [min && `min ${min}`, max && `max ${max}`].filter(Boolean).join(", ");
    parts.push(
      <span key="salary">
        <strong>Salary:</strong> {currency || "—"}
        {band ? ` (${band})` : ""}
      </span>
    );
  }
  if (!parts.length) return null;
  return parts.reduce((acc, node, idx) => {
    if (idx === 0) return [node];
    acc.push(" | ", node);
    return acc;
  }, []);
}

function ApplicantSearchPage({ currentUser, setCurrentUser, authToken }) {
  const { searchId } = useParams();
  const [row, setRow] = useState(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const t = String(authToken || "").trim();
    if (!t || !isApiConfigured()) {
      setRow(null);
      setLoadError("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await listApplicantSavedSearches(t);
        if (cancelled) return;
        const id = String(searchId || "").trim();
        const found = Array.isArray(rows) ? rows.find((r) => String(r.id) === id) : null;
        setRow(found || null);
        setLoadError("");
      } catch (e) {
        if (cancelled) return;
        setRow(null);
        setLoadError(e instanceof Error ? e.message : "Could not load saved searches.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken, searchId]);

  const when =
    row?.createdAt != null
      ? new Date(row.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
      : "";

  return (
    <ApplicantShell currentUser={currentUser} setCurrentUser={setCurrentUser}>
      <main className="jw-applicant-main applicant-home jw-applicant-saved-search-page">
        <section className="home-section">
          <Link className="back-link" to="/applicant-home">
            &larr; Back to applicant home
          </Link>
          {!isApiConfigured() || !String(authToken || "").trim() ? (
            <>
              <h1>Saved search</h1>
              <p className="subtitle">
                {isApiConfigured()
                  ? "You need to be signed in to load saved searches from the server."
                  : "Saved searches require the API to be configured."}
              </p>
            </>
          ) : loadError ? (
            <>
              <h1>Saved search</h1>
              <p className="subtitle">{loadError}</p>
            </>
          ) : row ? (
            <>
              <h1>{row.name}</h1>
              {when ? <p className="subtitle">{when}</p> : null}
              {row.criteria && typeof row.criteria === "object" ? (
                <p className="saved-search-meta saved-search-meta--criteria">{formatSavedSearchCriteria(row.criteria)}</p>
              ) : null}
              <p className="saved-search-meta">
                <Link
                  to="/job-search"
                  state={{
                    searchUiState: {
                      hasSearched: false,
                      executeSearchOnMount: true,
                      ...(row.criteria && typeof row.criteria === "object"
                        ? {
                            criteriaCountryCodes: row.criteria.criteriaCountryCodes,
                            criteriaWorkMode: row.criteria.criteriaWorkMode ?? row.criteria.workMode ?? "",
                            criteriaSalaryCurrency: row.criteria.criteriaSalaryCurrency,
                            criteriaTitles: row.criteria.criteriaTitles,
                            salaryMin: row.criteria.salaryMin,
                            salaryMax: row.criteria.salaryMax,
                            showNotForMe: row.criteria.showNotForMe,
                            showApplied: row.criteria.showApplied,
                            showSaved: row.criteria.showSaved,
                          }
                        : {}),
                    },
                  }}
                >
                  Run this search
                </Link>
              </p>
            </>
          ) : (
            <>
              <h1>Saved search not found</h1>
              <p className="subtitle">This saved search does not exist or is no longer available.</p>
            </>
          )}
        </section>
      </main>
    </ApplicantShell>
  );
}

export default ApplicantSearchPage;
